<script setup lang="ts">
// Métadonnées de la page de bienvenue
const userStore = useUserStore()
const modalStore = useModalStore()

false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    fr: '/bienvenue',
  },
})

definePageMeta({
  name: 'WelcomePage',
})

// Head configuration
useHead({
  title: 'Économiser et bien manger, c\'est notre recette | Glouton',
  meta: [
    { name: 'description', content: 'Dénicher les vraies aubaines, les raretés et les rabais cachés. Avec Glouton, économisez sur l\'épicerie et planifiez vos repas intelligemment. Gratuit, facile, efficace.' },
    { name: 'keywords', content: 'glouton, économiser épicerie, rabais, aubaines, recettes économiques, circulaires, application épicerie gratuite, planificateur repas, liste épicerie' },
    { property: 'og:title', content: 'Économiser et bien manger - Glouton' },
    { property: 'og:description', content: 'Découvrez Glouton : la plateforme qui combine les meilleures aubaines, recettes économiques et circulaires en temps réel. Économisez des milliers de dollars par année sur votre épicerie.' },
    { property: 'og:image', content: 'https://glouton.b-cdn.net/site/images/social.jpg' },
    { name: 'twitter:title', content: 'Économiser et bien manger avec Glouton' },
    { name: 'twitter:description', content: 'Les vraies aubaines, les raretés, les rabais cachés - tout en un. Gratuit pour tous, payant pour les fonctionnalités premium.' },
  ],
})

const config = useRuntimeConfig()

// Fetch media mentions from API
const { data: mediaMentions } = await useFetch(`${config.public.apiUrl}/home/fetch-media-mentions`, {
  transform: (response: any) => response.mentions || [],
})

const priceFeatures = [
  {
    id: 1,
    name: 'Identifier les meilleurs rabais',
    description:
      'Un score (sur 10) est attribué aux articles des circulaires pour vous aider à identifier rapidement les meilleures aubaines. Ce score est basé sur la saisonnalité, la fréquence et la générosité du rabais.',
    icon: 'i-mdi-cash',
    routeName: 'FlyersPage',
  },
  {
    id: 2,
    name: 'Encore plus d\'économies',
    description:
      'Notre outil Circulaires vous permet de consulter les rabais et de classer les plus avantageux en premier.',
    icon: 'i-mdi-tag-multiple',
    routeName: 'FlyersPage',
  },
  {
    id: 3,
    name: 'Fini le gaspillage',
    description:
      'Le vide-frigo vous propose des recettes en fonction de ce qu\'il vous reste. Un impact positif pour la planète et votre portefeuille.',
    icon: 'i-mdi-fridge',
    routeName: 'RecipesPage',
  },
  {
    id: 4,
    name: 'Des recettes avantageuses',
    description:
      'Notre outil L\'économiseur permet de trouver les recettes les plus avantageuses qui combinent les rabais des supermarchés sélectionnés.',
    icon: 'i-mdi-cash-register',
    routeName: 'MoneySaverPage',
  },
]

const timeFeatures = [
  {
    id: 1,
    name: 'Une liste d\'épicerie réinventée',
    description:
      'Votre liste d\'épicerie parfaite, toujours avec vous. Classez, modifiez et optimisez votre liste directement depuis votre téléphone à l\'épicerie.',
    icon: 'i-mdi-format-list-checkbox',
    routeName: 'ShoppingListPage',
  },
  {
    id: 2,
    name: 'Le Planificateur',
    description:
      'Préparez simplement vos menus dans un calendrier fait sur mesure. Importez des recettes depuis Glouton ou n\'importe quel site Web et ajoutez des notes pour ne rien oublier.',
    icon: 'i-mdi-calendar',
    routeName: 'MealPlannerPage',
  },
  {
    id: 3,
    name: 'Comme un livre de recettes',
    description:
      'Votre boîte de recettes vous permet de sauvegarder et classer vos recettes préférées et de les annoter, même celles importées depuis un site Web.',
    icon: 'i-mdi-archive-plus',
    routeName: 'RecipeBoxPage',
  },
  {
    id: 4,
    name: 'Suivez vos produits préférés',
    description:
      'Repérez facilement les meilleures aubaines sur vos produits favoris et soyez averti quand un produit est à rabais pour ne manquer aucune aubaine.',
    icon: 'i-mdi-magnify-scan',
    routeName: 'TrackedIngredientsPage',
  },
]

const faqs = [
  {
    question: 'Est-ce vraiment gratuit?',
    answer:
      'Oui! La consultation des circulaires avec nos scores, la liste d\'épicerie, le vide-frigo, et bien d\'autres outils sont entièrement gratuits. L\'abonnement Extra permet de maximiser vos économies de temps et d\'argent en déverrouillant des fonctionnalités comme le planificateur de menus, la boîte de recettes, et les produits suivis, en plus de retirer toutes les publicités.<br><br>Consultez la page Extra pour découvrir en détail toutes les différences entre l\'abonnement gratuit et payant. Une période d\'essai gratuite de 14 jours est offerte à tous sans même entrer de carte de crédit.',
    link: '/fr/extra',
    link_label: 'Découvrir l\'Extra',
  },
  {
    question: 'Quels sont les commerces analysés par Glouton?',
    answer:
      'Nous analysons les circulaires des 8 plus grands détaillants en alimentation au Québec: Metro, Super C, IGA, Maxi, Walmart, Provigo, Tigre Géant et Adonis. <br><br>Nous évaluons aussi les rabais des 5 plus importantes pharmacies: Jean Coutu, Pharmaprix, Brunet, Familiprix et Uniprix. <br><br>Les circulaires de nombreux autres détaillants sont également disponibles sans être évaluées. Nous prévoyons élargir notre couverture et analyser encore plus de commerces dans l\'avenir.',
  },
  {
    question: 'Pourquoi utiliser Glouton plutôt que Google ou Chat GPT?',
    answer:
      'C\'est une excellente question! Contrairement à Google ou ChatGPT qui donnent des résultats génériques, Glouton combine recettes, rabais en temps réel et votre localisation pour vous proposer les meilleures options économiques dans votre région. <br><br>Google ne connaît pas les prix actuels de vos épiceries, et ChatGPT ne peut pas analyser les circulaires du Québec. On analyse chaque semaine les promotions des principaux détaillants pour vous aider à économiser, en se basant sur plus de 7 ans d\'historique de prix.',
  },
  {
    question: 'Pourquoi Glouton plutôt qu\'une autre application?',
    answer:
      'Glouton est une application québécoise créée pour les Québécois.e.s. Nous comprenons bien mieux nos habitudes et nos besoins que les applications généralistes.<br><br>Nous n\'affichons pas simplement les prix... nous les évaluons! Les rabais des circulaires obtiennent un score qui vous aide à identifier les vraies aubaines en deux secondes, pas les faux rabais.<br><br>Contrairement à la plupart des compétiteurs, circulaires (y compris nos évaluations des rabais), liste d\'épicerie, recettes et vide-frigo sont gratuits pour tous. Pas de paiement caché sur les fonctionnalités essentielles. Notre abonnement payant offre des fonctionnalités supplémentaires liées à la planification et le retrait de la publicité.',
  },
]
</script>

<template>
  <div id="WelcomePage" class="relative">
    <!-- Animated background gradient -->
    <div class="absolute inset-0 bg-gradient-to-br from-tomate/5 via-bleuet/5 to-sauge/5" />

    <UContainer id="WelcomePage-anchor" class="pt-0 lg:pt-12 xl:pt-16">
      <!-- Top CTA Section -->
      <div class="relative -mx-4 lg:mx-0 py-10 lg:py-10 px-4 lg:px-0 bg-gradient-to-r from-tomate/10 via-bleuet/10 to-sauge/10 lg:rounded-2xl border-0 lg:border border-tomate/20 overflow-hidden">
        <div class="absolute inset-0 bg-gradient-to-br from-tomate/5 to-bleuet/5" />
        <div class="relative max-w-3xl mx-auto text-center">
          <p class="text-sm font-semibold text-tomate-600 uppercase tracking-wide mb-3">
            Bienvenue sur Glouton
          </p>
          <h2 class="text-3xl font-extrabold tracking-tight text-poivre-800 sm:text-4xl font-title bg-gradient-to-r from-tomate-600 to-bleuet-600 bg-clip-text text-transparent mb-4">
            Économiser et bien manger, c'est notre recette.
          </h2>
          <p class="text-lg text-poivre-600 mb-8 max-w-2xl mx-auto px-4 lg:px-0">
            Découvrez les meilleures aubaines, planifiez vos repas et gérez votre épicerie stratégiquement.
          </p>
          <div v-if="!userStore.user" class="flex flex-col sm:flex-row gap-4 justify-center items-center px-4 lg:px-0">
            <UButton
              :to="$localePath({ name: 'RegisterPage' })"
              color="primary"
              size="xl"
              class="transform hover:scale-105 transition-transform duration-300 shadow-lg hover:shadow-xl"
              icon="i-mdi-account-plus"
              label="Créer un compte gratuit"
            />
          </div>
        </div>
      </div>

      <!-- Background decorative elements -->
      <div class="relative overflow-hidden">
        <svg
          class="absolute transform -translate-x-1/2 lg:block left-full -translate-y-1/4 opacity-55"
          width="404"
          height="784"
          fill="none"
          viewBox="0 0 404 784"
          aria-hidden="true"
        >
          <defs>
            <pattern
              id="b1e6e422-73f8-40a6-b5d9-c8586e37e0e7"
              x="0"
              y="0"
              width="20"
              height="20"
              patternUnits="userSpaceOnUse"
            >
              <rect
                x="0"
                y="0"
                width="4"
                height="4"
                class="text-poivre-200"
                fill="currentColor"
              />
            </pattern>
          </defs>
          <rect width="404" height="784" fill="url(#b1e6e422-73f8-40a6-b5d9-c8586e37e0e7)" />
        </svg>

        <!-- Maximiser ses économies section -->
        <div class="relative mt-12 lg:mt-24 lg:grid lg:grid-cols-2 lg:gap-8 lg:items-center">
          <div class="relative">
            <div class="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-tomate/10 text-tomate-700 mb-6">
              <UIcon name="i-mdi-star" class="w-4 h-4 mr-2" />
              Économies intelligentes
            </div>
            <NuxtLink :to="$localePath({ name: 'FlyersPage' })">
              <h3 class="text-2xl font-extrabold tracking-tight text-poivre-800 sm:text-3xl font-title bg-gradient-to-r from-tomate-600 to-bleuet-600 bg-clip-text text-transparent">
                Maximisez vos économies
              </h3>
            </NuxtLink>
            <p class="mt-3 text-lg text-poivre-500">
              Notre analyse des prix vous aide à dénicher les vraies aubaines, les raretés et les rabais cachés.
            </p>

            <dl class="mt-10 space-y-10">
              <div v-for="item in priceFeatures" :key="item.id" class="relative group">
                <dt>
                  <div class="absolute flex items-center justify-center w-12 h-12 text-white bg-gradient-to-br from-tomate-400 to-tomate-600 rounded-xl shadow-lg group-hover:scale-105 transition-transform duration-300">
                    <NuxtLink :to="$localePath({ name: item.routeName })" class="w-full h-full flex items-center justify-center">
                      <UIcon :name="item.icon" class="w-6 h-6" aria-hidden="true" />
                    </NuxtLink>
                  </div>
                  <NuxtLink :to="$localePath({ name: item.routeName })">
                    <p class="ml-16 text-lg font-medium leading-6 text-poivre-800 group-hover:text-tomate-600 transition-colors duration-300">
                      {{ item.name }}
                    </p>
                  </NuxtLink>
                </dt>
                <dd class="mt-2 ml-16 text-base text-poivre-500">
                  {{ item.description }}
                  <NuxtLink v-if="item.routeName" :to="$localePath({ name: item.routeName })" class="inline-flex items-center text-tomate-400 hover:text-tomate-500 transition-colors duration-300 group-hover:translate-x-1">
                    <UIcon name="i-mdi-arrow-right-thin" class="inline ml-1" />
                  </NuxtLink>
                </dd>
              </div>
            </dl>
          </div>

          <div class="relative mt-10 -mx-4 lg:mt-0" aria-hidden="true">
            <svg class="absolute transform -translate-x-1/2 translate-y-16 left-1/2 lg:hidden" width="784" height="404" fill="none" viewBox="0 0 784 404">
              <defs>
                <pattern
                  id="ca9667ae-9f92-4be7-abcb-9e3d727f2941"
                  x="0"
                  y="0"
                  width="20"
                  height="20"
                  patternUnits="userSpaceOnUse"
                >
                  <rect
                    x="0"
                    y="0"
                    width="4"
                    height="4"
                    class="text-poivre-200"
                    fill="currentColor"
                  />
                </pattern>
              </defs>
              <rect width="784" height="404" fill="url(#ca9667ae-9f92-4be7-abcb-9e3d727f2941)" />
            </svg>
            <div class="relative mx-auto w-3/4 sm:w-1/2 transform hover:scale-105 transition-transform duration-200">
              <div class="absolute inset-0 bg-gradient-to-br from-tomate/20 to-bleuet/20 rounded-2xl blur-xl" />
              <div class="relative rounded-2xl border-2 border-tomate/30 shadow-2xl overflow-hidden" style="position:relative;padding-top:175%;">
                <!-- Header bar -->
                <div class="absolute top-0 left-0 right-0 h-8 bg-gradient-to-b from-black/60 to-transparent justify-center flex items-center px-2 w-full z-2">
                  <h3 class="text-sm font-normal text-white text-center">
                    Présentation par Eve-Lyne Auger (<a href="https://www.instagram.com/lafraiche__/" target="_blank" class="text-white hover:text-gray-300">@lafraiche__</a>)
                  </h3>
                </div>
                <iframe src="https://player.mediadelivery.net/embed/93705/4c1d7276-6489-4292-8036-98f06a96d5ac?autoplay=true&loop=false&muted=false&preload=true&responsive=true" loading="lazy" style="border:0;position:absolute;top:0;height:100%;width:100%;border-radius:0.5rem;" allow="accelerometer;gyroscope;encrypted-media;picture-in-picture;" allowfullscreen="true" />
              </div>
            </div>
          </div>
        </div>

        <svg
          class="absolute hidden transform translate-x-1/2 translate-y-12 lg:block right-full opacity-55"
          width="404"
          height="784"
          fill="none"
          viewBox="0 0 404 784"
          aria-hidden="true"
        >
          <defs>
            <pattern
              id="64e643ad-2176-4f86-b3d7-f2c5da3b6a6d"
              x="0"
              y="0"
              width="20"
              height="20"
              patternUnits="userSpaceOnUse"
            >
              <rect
                x="0"
                y="0"
                width="4"
                height="4"
                class="text-poivre-200"
                fill="currentColor"
              />
            </pattern>
          </defs>
          <rect width="404" height="784" fill="url(#64e643ad-2176-4f86-b3d7-f2c5da3b6a6d)" />
        </svg>

        <!-- Plus de temps pour vous section -->
        <div class="relative mt-12 sm:mt-16 lg:mt-24">
          <div class="lg:grid lg:grid-flow-row-dense lg:grid-cols-2 lg:gap-8 lg:items-center">
            <div class="lg:col-start-2">
              <div class="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-bleuet/10 text-bleuet-700 mb-6">
                <UIcon name="i-mdi-clock-outline" class="w-4 h-4 mr-2" />
                Organisation efficace
              </div>
              <NuxtLink :to="$localePath({ name: 'ShoppingListPage' })">
                <h3 class="text-2xl font-extrabold tracking-tight text-poivre-800 sm:text-3xl font-title bg-gradient-to-r from-bleuet-600 to-sauge-600 bg-clip-text text-transparent">
                  Plus de temps pour vous
                </h3>
              </NuxtLink>
              <p class="mt-3 text-lg text-poivre-500">
                Une bonne organisation est la clé pour économiser du temps et Glouton vous aide à y arriver.
              </p>

              <dl class="mt-10 space-y-10">
                <div v-for="item in timeFeatures" :key="item.id" class="relative group">
                  <dt>
                    <div class="absolute flex items-center justify-center w-12 h-12 text-white bg-gradient-to-br from-bleuet-400 to-bleuet-600 rounded-xl shadow-lg group-hover:scale-105 transition-transform duration-300">
                      <NuxtLink :to="$localePath({ name: item.routeName })" class="w-full h-full flex items-center justify-center">
                        <UIcon :name="item.icon" class="w-6 h-6" aria-hidden="true" />
                      </NuxtLink>
                    </div>
                    <NuxtLink :to="$localePath({ name: item.routeName })">
                      <p class="ml-16 text-lg font-medium leading-6 text-poivre-800 group-hover:text-bleuet-600 transition-colors duration-300">
                        {{ item.name }}
                      </p>
                    </NuxtLink>
                  </dt>
                  <dd class="mt-2 ml-16 text-base text-poivre-500">
                    {{ item.description }}
                    <NuxtLink v-if="item.routeName" :to="$localePath({ name: item.routeName })" class="inline-flex items-center text-bleuet-400 hover:text-bleuet-500 transition-colors duration-300 group-hover:translate-x-1">
                      <UIcon name="i-mdi-arrow-right-thin" class="inline ml-1" />
                    </NuxtLink>
                  </dd>
                </div>
              </dl>
            </div>

            <div class="relative mt-10 lg:-mx-4 lg:mt-0 lg:col-start-1">
              <svg
                class="absolute transform -translate-x-1/2 translate-y-16 left-1/2 hidden"
                width="784"
                height="404"
                fill="none"
                viewBox="0 0 784 404"
                aria-hidden="true"
              >
                <defs>
                  <pattern
                    id="e80155a9-dfde-425a-b5ea-1f6fadd20131"
                    x="0"
                    y="0"
                    width="20"
                    height="20"
                    patternUnits="userSpaceOnUse"
                  >
                    <rect
                      x="0"
                      y="0"
                      width="4"
                      height="4"
                      class="text-poivre-200"
                      fill="currentColor"
                    />
                  </pattern>
                </defs>
                <rect width="784" height="404" fill="url(#e80155a9-dfde-425a-b5ea-1f6fadd20131)" />
              </svg>
              <div class="relative mx-auto w-full lg:w-7/8 transform hover:scale-105 transition-transform duration-300">
                <div class="hidden lg:block absolute inset-0 bg-gradient-to-br from-bleuet/20 to-sauge/20 rounded-2xl blur-xl" />
                <img class="relative rounded-2xl border-2 border-bleuet/30 shadow-2xl" src="https://glouton.b-cdn.net/site/images/mockups/welcome_shopping_list.jpg" alt="">
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Call to action section -->
      <div class="relative mt-20 bg-gradient-to-r from-tomate/10 via-bleuet/10 to-sauge/10 rounded-3xl border border-tomate/20 overflow-hidden">
        <div class="absolute inset-0 bg-gradient-to-br from-tomate/5 to-bleuet/5" />
        <div class="relative max-w-8xl mx-auto text-center py-16 px-4 sm:px-6 lg:py-20 lg:px-8">
          <h2 class="text-3xl font-extrabold tracking-tight text-poivre-800 sm:text-4xl font-title bg-gradient-to-r from-tomate-600 to-bleuet-600 bg-clip-text text-transparent">
            <span class="block">Prêt à commencer?</span>
          </h2>
          <p class="mt-4 text-lg text-poivre-600 max-w-2xl mx-auto">
            Rejoignez des milliers de membres qui économisent déjà temps et argent avec Glouton.
          </p>
          <div v-if="!userStore.user" class="mt-8 flex flex-col sm:flex-row gap-4 justify-center items-center">
            <UButton
              :to="$localePath({ name: 'RegisterPage' })"
              color="primary"
              size="xl"
              class="transform hover:scale-105 transition-transform duration-300 shadow-lg hover:shadow-xl"
              icon="i-mdi-account-plus"
              label="Créer un compte gratuit"
            />
            <UButton
              variant="ghost"
              color="primary"
              size="lg"
              class=""
              @click="modalStore.openLoginModal"
            >
              <UIcon name="i-mdi-login" class="w-5 h-5 mr-2" />
              J'ai déjà un compte
            </UButton>
          </div>
          <div v-else class="mt-8 flex flex-col sm:flex-row gap-4 justify-center items-center text-sauge-500">
            Vous êtes déjà connecté. C'est l'heure de commencer à économiser!
          </div>
        </div>
      </div>

      <!-- Media section -->
      <div class="w-full mx-auto pt-16 lg:pt-20">
        <div class="text-center mb-12">
          <div class="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-sauge/10 text-sauge-700 mb-6">
            <UIcon name="i-mdi-newspaper" class="w-4 h-4 mr-2" />
            Reconnaissance médiatique
          </div>
          <h2 class="text-3xl font-extrabold text-center text-poivre-800 sm:text-4xl font-title bg-gradient-to-r from-sauge-600 to-tomate-600 bg-clip-text text-transparent">
            Dans les médias
          </h2>
          <p class="mt-4 text-lg text-poivre-600 max-w-2xl mx-auto">
            Découvrez ce que les médias québécois disent de Glouton
          </p>
        </div>
        <UCarousel
          v-slot="{ item }"
          :items="mediaMentions"
          :autoplay="{ delay: 3000 }"
          class="w-full"
          :ui="{
            item: 'basis-full sm:basis-1/2 lg:basis-1/3',
            viewport: '',
            container: ' items-stretch ',
            dot: 'bg-sauge-200 data-[state=active]:bg-sauge-500 size-2',
          }"
          :prev="{ color: 'sauge' }"
          :next="{ color: 'sauge' }"
          dots
        >
          <div class="group bg-white/50 backdrop-blur-sm rounded-2xl p-6 border border-poivre-200 hover:border-tomate-300  flex flex-col h-full">
            <a :href="item.link" target="_blank" class="flex justify-center mb-4 group-hover:scale-105 transition-transform duration-300 flex-shrink-0">
              <img class="max-h-14" :src="item.logo_path" :alt="item.media_name">
            </a>
            <div class="text-poivre-800 text-sm text-center flex-grow">
              {{ item.quote }}
            </div>
          </div>
        </UCarousel>
      </div>

      <!-- FAQ section -->
      <div class="px-4 py-16 mx-auto max-w-8xl sm:pt-20 sm:px-6 lg:px-8">
        <div class="max-w-4xl mx-auto">
          <div class="text-center mb-12">
            <div class="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-bleuet/10 text-bleuet-700 mb-6">
              <UIcon name="i-mdi-help-circle" class="w-4 h-4 mr-2" />
              Support et aide
            </div>
            <h2 class="text-3xl font-extrabold text-center text-poivre-800 sm:text-4xl font-title bg-gradient-to-r from-bleuet-600 to-sauge-600 bg-clip-text text-transparent">
              Questions fréquentes
            </h2>
            <p class="mt-4 text-lg text-poivre-600 max-w-2xl mx-auto">
              Trouvez rapidement les réponses à vos questions les plus courantes
            </p>
          </div>
          <div class="bg-white/50 backdrop-blur-sm rounded-3xl border border-poivre-200 p-8 shadow-lg">
            <UAccordion
              :items="faqs.map(faq => ({
                label: faq.question,
                content: faq.answer,
                link: faq.link,
                link_label: faq.link_label,
              }))"
              class="divide-y divide-poivre-100"
              :ui="{ trigger: 'text-base', body: 'text-base' }"
            >
              <template #content="{ item }">
                <p class="pb-3.5 text-base text-muted" v-html="item.content" />
                <UButton v-if="item.link" :label="item.link_label" :to="item.link" color="primary" size="sm" class="mb-3.5" />
              </template>
            </UAccordion>
          </div>
        </div>
      </div>
    </UContainer>
  </div>
</template>
